/* «Свой референс» (BotRef, APP-SPEC-5 §1.10): первая карточка ленты. Пусто — пунктир, нажатие сразу открывает выбор файла;
   проверка — «Проверяем»; выбрано — кольцо, метка «Ваша» (у нескольких — мозаика и «N фото»), под лентой плашка
   с файлом или рядом миниатюр и строка со щитом. Выбор другого стиля гасит плашку, картинка остаётся на карточке.
   Загрузка — POST /app/uploads с назначением reference (политика поля формы), в заказ уходят только id выбранных. */
import { uiFormat, uiText } from '../../../i18n';
import type { useStudioForm } from '../../studio/use-studio-form';
import { StudioIcon } from '../../studio/StudioIcon';
import { REFERENCE_ACCEPT, type ReferenceModel } from './use-reference';

type FormState = ReturnType<typeof useStudioForm>;

export function ReferenceCard({
  state,
  model,
  title = 'Загрузить картинку',
  label = 'Свой референс',
  onSelected,
  onDeselected,
}: {
  state: FormState;
  model: ReferenceModel;
  title?: string;
  label?: string;
  onSelected(): void;
  onDeselected?(): void;
}) {
  const pictures = model.stored.map(slot => model.local[slot.key]?.url).filter((url): url is string => !!url);
  const empty = !model.stored.length;
  return (
    <>
      <button
        type="button"
        className={`bcs-c shk-card shk-ref${model.on ? ' on' : ''}${empty ? ' dash' : ''}`}
        aria-pressed={model.on}
        aria-label={uiText(label)}
        disabled={state.locked || model.checking}
        onClick={() => {
          if (empty) model.open();
          else if (!model.on) {
            model.select(true);
            onSelected();
          } else {
            model.select(false);
            onDeselected?.();
          }
        }}
      >
        <span className={`pc${pictures.length > 1 ? ' mos' : ''}`}>
          {pictures.length ? (
            pictures.slice(0, model.multi ? 9 : 1).map(url => <img key={url} src={url} alt="" draggable={false} />)
          ) : (
            <span className="bcs-no">
              <StudioIcon kind={empty ? 'plus' : 'image'} />
              {empty && <small>{uiText(title)}</small>}
            </span>
          )}
          {!empty && <span className="bcs-badge">{model.multi ? uiFormat('{0} фото', model.stored.length) : uiText('Ваша')}</span>}
          {model.checking && (
            <span className="shk-checking" role="status">
              {uiText('Проверяем')}
            </span>
          )}
          <i className="ck" aria-hidden="true">
            <StudioIcon kind="check" />
          </i>
        </span>
        <b>{uiText(label)}</b>
      </button>
      <input
        id={model.inputId}
        className="studio-native-file-input"
        type="file"
        accept={REFERENCE_ACCEPT.join(',')}
        multiple={model.multi}
        aria-label={uiFormat('Файл: {0}', uiText(label))}
        disabled={state.locked}
        onChange={event => {
          const files = event.target.files;
          void model.add(files).then(added => {
            if (added) {
              model.select(true);
              onSelected();
            }
          });
          event.target.value = '';
        }}
      />
    </>
  );
}

/** Плашка под лентой: строка файла или ряд миниатюр, щит — что берём с картинки. */
export function ReferencePlaque({ state, model, shield }: { state: FormState; model: ReferenceModel; shield: string }) {
  if (!model.on) return null;
  const first = model.stored[0];
  const info = first ? model.local[first.key] : undefined;
  const full = model.stored.length >= model.slots.length;
  return (
    <div className="shk-refbar">
      {model.multi ? (
        <>
          <div className="shk-refbar-h">
            <b>
              {uiFormat('{0} фото', model.stored.length)} · {uiFormat('{0} кадров', model.stored.length)}
            </b>
            <button
              type="button"
              className="lnk"
              aria-disabled={full}
              disabled={state.locked}
              title={full ? uiFormat('Можно до {0} фото', model.slots.length) : undefined}
              onClick={() => {
                if (!full) model.open();
              }}
            >
              {uiText('Ещё')}
            </button>
            <button type="button" className="lnk" disabled={state.locked} onClick={model.clear}>
              {uiText('Убрать')}
            </button>
          </div>
          <div className="shk-refbar-row">
            {model.slots.map((slot, index) =>
              model.local[slot.key] ? (
                <img key={slot.key} src={model.local[slot.key].url} alt={uiFormat('Сцена {0}', index + 1)} />
              ) : state.inputs[slot.key] ? (
                <span key={slot.key} className="ph" role="img" aria-label={uiFormat('Сцена {0}', index + 1)} />
              ) : (
                <button key={slot.key} type="button" className="ph dash" aria-label={uiText('Добавить')} disabled={state.locked} onClick={model.open}>
                  <StudioIcon kind="plus" />
                </button>
              ),
            )}
          </div>
        </>
      ) : (
        <div className="shk-refbar-h">
          {info && <img src={info.url} alt="" />}
          <span className="m">
            <b>{info?.name ?? uiText('Ваша картинка')}</b>
            {info && <small>{uiFormat('{0} МБ', (info.size / 1024 / 1024).toLocaleString('ru', { maximumFractionDigits: 1 }))}</small>}
          </span>
          <button
            type="button"
            className="lnk"
            disabled={state.locked}
            onClick={() => {
              // повторная загрузка в занятый слот только проверила бы прежний файл: слот сначала освобождается
              model.clear();
              model.open();
            }}
          >
            {uiText('Заменить')}
          </button>
          <button type="button" className="lnk" disabled={state.locked} onClick={model.clear}>
            {uiText('Убрать')}
          </button>
        </div>
      )}
      <p className="bc-cap shk-cap">
        <StudioIcon kind="eye" />
        {uiText(shield)}
      </p>
    </div>
  );
}
